import { trackAnalyticsEvent } from '@autone/analytics';
import { useAutoneTranslation } from '@autone/translations';
import { createJobPollingHook } from '@autone/utils';
import { enqueueSnackbar } from 'notistack';
import { useCallback } from 'react';

import { type APP_NAME } from '../consts';
import {
  POLLING_INTERVAL_SECONDS,
  POLLING_TIMEOUT_SECONDS,
} from '../consts/polling-interval';
import {
  useCreateAssortmentTemplateExportJobMutation,
  usePollAssortmentTemplateExportJobQuery,
} from '../redux/assortment';

const useCreateAssortmentTemplateExportJob = createJobPollingHook({
  useCreateJobMutation: useCreateAssortmentTemplateExportJobMutation,
  usePollJobQuery: usePollAssortmentTemplateExportJobQuery,
  getPollJobIsSuccess: (pollJobResult) => pollJobResult.status === 'completed',
  getPollJobIsError: (pollJobResult) => pollJobResult.status === 'failed',
  getPollJobQueryFromCreateJobResult: (createJobResult) => ({
    path: {
      job_id: createJobResult.id,
    },
  }),
  getAreSameJobs: (createJobResult, pollJobResult) =>
    createJobResult.id === pollJobResult.id,
  pollingIntervalSeconds: POLLING_INTERVAL_SECONDS,
  pollingTimeoutSeconds: POLLING_TIMEOUT_SECONDS,
});

interface UseAssortmentTemplateExportProps {
  onExportReady: (url: string) => void;
  exportRowCount: number | null;
}

export const useAssortmentTemplateExport = ({
  onExportReady,
  exportRowCount,
}: UseAssortmentTemplateExportProps) => {
  const { common } = useAutoneTranslation<typeof APP_NAME>();

  const {
    createJob,
    cancelJob,
    jobIsRunning,
    jobIsSuccess,
    jobIsError,
    pollJobResult,
  } = useCreateAssortmentTemplateExportJob({
    onJobSuccess: ({ createJobOriginalArgs, durationSeconds }) => {
      const { aggregation_type } = createJobOriginalArgs?.body ?? {};
      trackAnalyticsEvent({
        category: 'assortment_template_import_export',
        action: 'download_template_modal:download_succeeded',
        properties: {
          aggregation_type,
          duration_seconds: durationSeconds,
          row_count: exportRowCount,
        },
      });
      onExportReady(pollJobResult?.s3_url ?? '');
    },
    onJobError: () => {
      enqueueSnackbar({ message: common('export-failed'), variant: 'error' });
    },
  });

  const downloadTemplateExport = useCallback(
    async (...args: Parameters<typeof createJob>) => {
      if (exportRowCount === null) return;
      await createJob(...args);
    },
    [createJob, exportRowCount],
  );

  const cancelDownload = useCallback(() => {
    cancelJob();
  }, [cancelJob]);

  // combined export hook states
  const exportIsError = jobIsError;
  const exportIsSuccess = jobIsSuccess;
  const exportIsLoading = jobIsRunning;

  return {
    downloadTemplateExport,
    cancelDownload,
    exportIsLoading,
    exportIsSuccess,
    exportIsError,
  };
};
